import { ThemeProvider } from "@emotion/react";
import {
  createTheme,
  CssBaseline,
  GlobalStyles,
  Snackbar,
  Stack,
  useMediaQuery,
} from "@mui/material";
import React, { useState } from "react";

import Header from "./Header";
import Main from "./Main";
import ProgressDialog from "./ProgressDialog";
import { TransferQueueProvider } from "./app/transferQueue";

const globalStyles = (
  <GlobalStyles styles={{ "html, body, #root": { height: "100%" } }} />
);

function App() {
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode: prefersDark ? "dark" : "light",
          primary: { main: "#07c160", contrastText: "#ffffff" },
          ...(prefersDark
            ? {
                background: { default: "#111214", paper: "#1c1e21" },
              }
            : {
                background: { default: "#f5f6f8", paper: "#ffffff" },
              }),
        },
        shape: { borderRadius: 12 },
        typography: {
          fontFamily: [
            "-apple-system",
            "BlinkMacSystemFont",
            '"PingFang SC"',
            '"Microsoft YaHei"',
            '"Segoe UI"',
            "Roboto",
            "sans-serif",
          ].join(","),
        },
        components: {
          MuiPaper: {
            styleOverrides: { rounded: { borderRadius: 16 } },
          },
          MuiDialog: {
            styleOverrides: { paper: { borderRadius: 16 } },
          },
          MuiDrawer: {
            styleOverrides: {
              paper: {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: { borderRadius: 20, textTransform: "none" },
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: {
                "&.Mui-selected": {
                  backgroundColor: "rgba(7, 193, 96, 0.10)",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "rgba(7, 193, 96, 0.16)",
                },
              },
            },
          },
          MuiFab: {
            styleOverrides: {
              root: {
                boxShadow: "0 4px 14px rgba(0, 0, 0, 0.18)",
              },
            },
          },
        },
      }),
    [prefersDark]
  );
  const [search, setSearch] = useState("");
  const [showProgressDialog, setShowProgressDialog] = React.useState(false);
  const [error, setError] = useState<Error | null>(null);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      <TransferQueueProvider>
        <Stack sx={{ height: "100%" }}>
          <Header
            search={search}
            onSearchChange={(newSearch: string) => setSearch(newSearch)}
            setShowProgressDialog={setShowProgressDialog}
          />
          <Main search={search} onError={setError} />
        </Stack>
        <Snackbar
          autoHideDuration={5000}
          open={Boolean(error)}
          message={error?.message}
          onClose={() => setError(null)}
        />
        <ProgressDialog
          open={showProgressDialog}
          onClose={() => setShowProgressDialog(false)}
        />
      </TransferQueueProvider>
    </ThemeProvider>
  );
}

export default App;
